import { useState } from "react";
import { toast } from "sonner";
import { ArrowDownRight, ShieldCheck, Pencil } from "lucide-react";
import { WorkspaceShell } from "@/shell/WorkspaceShell";
import { Card, CardBody, Pill, Button, EmptyState, Avatar, ProgressBar } from "@/design/components";
import {
  useSeed,
  useMyAuthority,
  useAuthorityGrants,
  usePeopleIndex,
  useOrgIndex,
  useActiveProfile,
  useMoney,
} from "@/domain/hooks";
import { useUpdateGrantThreshold } from "@/domain/useMutations";
import type { AuthorityGrant, WorkspaceSeed } from "@/domain/types";

/** Spend committed by a grantee against the grant's scope (excludes rejected). */
function burnForGrant(seed: WorkspaceSeed, g: AuthorityGrant): number {
  return seed.spendRequests
    .filter((r) => r.reporterId === g.granteeId && r.subProjectId === g.scope.subProjectId && r.state !== "rejected")
    .reduce((a, r) => a + r.amount, 0);
}

/**
 * Spending authority (v3 §Chunk 1). Authority and budget cascade down the chain: each
 * holder sees the envelope they were granted and sets the self-authorisation threshold
 * for the people and subcontractors beneath them. Below a person's threshold, spend is
 * recorded-and-evidenced directly; above it, it is sent up the chain for authorisation.
 */
export function SpendingAuthority({ embedded = false }: { embedded?: boolean }) {
  const profile = useActiveProfile();
  const { held, delegated } = useMyAuthority();
  const allGrants = useAuthorityGrants();
  const isContractor = profile.group === "contractor";

  const inner = (
    <div className={embedded ? "" : "px-4 py-5 md:px-8 md:py-6"}>
      {!embedded && <h1 className="text-2xl font-semibold tracking-tight text-ink">Spending authority</h1>}
      <p className={embedded ? "text-sm text-mute" : "mt-1 text-sm text-mute"}>
        Budget and authority cascade down the chain. Each level sets the threshold under which the level
        below may spend directly; above it, spend is sent up for authorisation.
      </p>

      {isContractor ? (
        <div className="mt-6 space-y-8">
          <HeldSection grants={held} />
          <DelegatedSection grants={delegated} />
        </div>
      ) : (
        <div className="mt-6">
          <CascadeOverview grants={allGrants} />
        </div>
      )}
    </div>
  );

  return embedded ? inner : <WorkspaceShell>{inner}</WorkspaceShell>;
}

function HeldSection({ grants }: { grants: AuthorityGrant[] }) {
  const people = usePeopleIndex();
  const money = useMoney();
  const seed = useSeed();
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-faint">Authority you hold</h2>
      {grants.length === 0 ? (
        <EmptyState title="No delegated authority yet" body="When a prime contractor grants you an envelope and a threshold, it appears here." />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {grants.map((g) => (
            <Card key={g.id}>
              <CardBody>
                <div className="flex items-center justify-between">
                  <Pill variant="brand">{g.scope.label ?? "Scope"}</Pill>
                  <span className="flex items-center gap-1 text-xs text-faint">
                    <ShieldCheck className="h-3.5 w-3.5" /> granted by {people[g.granterId]?.fullName ?? g.granterId}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Figure label="Budget envelope" value={money(g.envelope)} />
                  <Figure label="You may spend up to" value={money(g.selfAuthThreshold)} />
                </div>
                {(() => {
                  const burn = burnForGrant(seed, g);
                  return (
                    <div className="mt-3">
                      <ProgressBar value={g.envelope ? burn / g.envelope : 0} />
                      <p className="mt-1 text-xs text-faint">{money(burn)} committed of {money(g.envelope)}</p>
                    </div>
                  );
                })()}
                {g.scope.zone && <p className="mt-2 text-xs text-faint">Zone: {g.scope.zone}</p>}
                <p className="mt-2 text-xs leading-snug text-mute">
                  At or under {money(g.selfAuthThreshold)} you record-and-evidence directly. Above it, the spend is sent up for authorisation.
                </p>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

function DelegatedSection({ grants }: { grants: AuthorityGrant[] }) {
  const people = usePeopleIndex();
  const orgs = useOrgIndex();
  const money = useMoney();
  const { updateThreshold, isPending: saving } = useUpdateGrantThreshold();
  const seed = useSeed();
  const [editing, setEditing] = useState<string | null>(null);
  const [value, setValue] = useState("");

  async function save(g: AuthorityGrant) {
    const n = Number(value.replace(/[^0-9]/g, ""));
    if (n > 0) {
      await updateThreshold(g.id, n);
      toast.success(`Updated ${people[g.granteeId]?.fullName ?? "the grantee"}'s threshold to ${money(n)}`);
    }
    setEditing(null);
    setValue("");
  }

  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-faint">Authority you delegate</h2>
      {grants.length === 0 ? (
        <EmptyState title="You have not delegated authority" body="Grant a member of your team or a subcontractor an envelope and a spend threshold to let them act within your budget." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-line bg-card">
          {grants.map((g) => (
            <div key={g.id} className="flex flex-wrap items-center gap-3 border-b border-hairline px-4 py-3 last:border-0">
              <Avatar initials={people[g.granteeId]?.avatarInitials ?? "?"} size={32} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink">{people[g.granteeId]?.fullName ?? g.granteeId}</p>
                <p className="text-xs text-faint">{orgs[g.granteeOrgId]?.name} · {g.scope.label ?? "scope"}{g.scope.zone ? ` · ${g.scope.zone}` : ""}</p>
              </div>
              <div className="w-32 text-right">
                <p className="text-[11px] uppercase tracking-wider text-faint">Envelope used</p>
                <p className="text-sm font-medium tabular-nums text-ink">{money(burnForGrant(seed, g))} / {money(g.envelope)}</p>
                <ProgressBar value={g.envelope ? burnForGrant(seed, g) / g.envelope : 0} className="mt-1" />
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-wider text-faint">Self-auth up to</p>
                {editing === g.id ? (
                  <span className="flex items-center gap-1">
                    <input
                      autoFocus
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      inputMode="numeric"
                      placeholder={String(g.selfAuthThreshold)}
                      className="w-28 rounded-md border border-line px-2 py-1 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand"
                    />
                    <Button size="sm" onClick={() => save(g)}>Save</Button>
                  </span>
                ) : (
                  <button onClick={() => { setEditing(g.id); setValue(""); }} className="flex items-center gap-1 text-sm font-medium tabular-nums text-ink hover:text-brand">
                    {money(g.selfAuthThreshold)} <Pencil className="h-3 w-3 text-faint" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Read-only cascade for oversight / funder: who delegates what to whom across the programme. */
function CascadeOverview({ grants }: { grants: AuthorityGrant[] }) {
  const people = usePeopleIndex();
  const orgs = useOrgIndex();
  const money = useMoney();
  if (grants.length === 0) {
    return <EmptyState title="No delegated authority recorded" body="Contractor spending thresholds will appear here once delegated." />;
  }
  // Group by granter to show the cascade levels.
  const byGranter = new Map<string, AuthorityGrant[]>();
  for (const g of grants) byGranter.set(g.granterId, [...(byGranter.get(g.granterId) ?? []), g]);

  return (
    <div className="space-y-5">
      {[...byGranter.entries()].map(([granterId, gs]) => (
        <div key={granterId} className="rounded-lg border border-line bg-card p-4">
          <p className="text-sm font-semibold text-ink">{people[granterId]?.fullName ?? granterId}</p>
          <p className="text-xs text-faint">{orgs[people[granterId]?.organisationId ?? ""]?.name} delegates to</p>
          <ul className="mt-3 space-y-1.5">
            {gs.map((g) => (
              <li key={g.id} className="flex flex-wrap items-center gap-2 rounded-md border border-hairline px-3 py-2 text-sm">
                <ArrowDownRight className="h-4 w-4 text-faint" />
                <span className="font-medium text-ink">{people[g.granteeId]?.fullName ?? g.granteeId}</span>
                <Pill variant="neutral">{g.scope.label ?? "scope"}</Pill>
                <span className="ml-auto flex items-center gap-4 text-xs text-faint">
                  <span>envelope <span className="font-medium tabular-nums text-ink">{money(g.envelope)}</span></span>
                  <span>self-auth ≤ <span className="font-medium tabular-nums text-ink">{money(g.selfAuthThreshold)}</span></span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-faint">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums text-ink">{value}</p>
    </div>
  );
}
